import { yupResolver } from '@hookform/resolvers/yup';
import ClearIcon from '@mui/icons-material/Clear';
import { Card, FormControl, FormLabel, Grid, Typography } from '@mui/material';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { esES } from '@mui/x-date-pickers/locales';

import { PermissionsEnum } from '@/auth/interfaces';
import { gridSizeMdLg6, ROUTER_PATHS, useCheckPermissions, useLoaders } from '@/shared';
import {
  useCreatePlanificador,
  useFetchPlanificadores,
  useGetFlota,
} from '@/shared/actions/erp';
import {
  LoaderComponent,
  SampleRadioButtonsGroup,
  SampleWeekPicker,
  SingleFormBoxScene,
  SingleIconButton,
} from '@/shared/components/ui';
import {
  AlertSeverityEnum,

} from '@/shared/interfaces';
import { createNewPlanificadorSchema } from '@/shared/utils/validation-schema';
import { useUiStore } from '@/store/ui';
import { RadioButtonOptions } from '.';
import { CreatePlanificadorForm, TipoPlanificador } from '@/shared/interfaces/erp/mantenimiento-operaciones';

export interface SavePlanificadorProps {
  titlePage: string;
  idFlota: string;

}

const SavePlanificador: React.FC<SavePlanificadorProps> = ({ titlePage, idFlota }) => {
  useCheckPermissions(PermissionsEnum.api_view_flota);
  const returnUrlPlanificadoresPage =
    ROUTER_PATHS.mantenimientoOperaciones.planificadorNav;
  const navigate = useNavigate();

  ///* local state -----------------------
  const [minDate, setMinDate] = useState<any | null>(null);
  const [initDateManual, setInitDateManual] = useState<Dayjs | null>(null);
  const [endDateManual, setEndDateManual] = useState<Dayjs | null>(null);
  const [minDateManual, setMinDateManual] = useState<Dayjs | null>(null);

  ///* global state -----------------------
  const setAlert = useUiStore(s => s.setAlert);

  ///* form -----------------------
  const form = useForm<CreatePlanificadorForm>({
    resolver: yupResolver(createNewPlanificadorSchema),
    defaultValues: {
      tipoPlanificador: TipoPlanificador.AUTOMATICO,
      flota: idFlota,
    },
  });
  const {
    handleSubmit,
    formState: { errors },
  } = form;

  const watchedTipoPlanificador = form.watch('tipoPlanificador');
  const watchedFlota = idFlota
  const watchedFechasArrObj = form.watch('fechasArrObj');

  ///* fetch data -----------------------
  const {
    data: planificadoresByFlotaPagingRes,
    isLoading: isLoadingPlanificador,
    isRefetching: isRefetchingPlanificador,
  } = useFetchPlanificadores({
    enabled: !!watchedFlota,
    params: {
      flota: watchedFlota,
      page_size: 150,
    },
  });

  ///* mutations -----------------------
  const {
    mutateAsync,
    isPending,
  } = useCreatePlanificador({
    enableRedirect: false,
  });

  const { data: dataFlota, isLoading: isLoadingFlota } = useGetFlota(idFlota)

  ///* handlers -----------------------
  const onSave = async (data: CreatePlanificadorForm) => {
    if (data.tipoPlanificador === TipoPlanificador.MANUAL) {
      if (!initDateManual)
        return setAlert(
          true,
          'La fecha inicial es requerida',
          AlertSeverityEnum.error
        );
      if (!endDateManual)
        return setAlert(
          true,
          'La fecha final es requerida',
          AlertSeverityEnum.error
        );
    }

    const monday = dayjs(data.fecha_inicio).day(1).format('YYYY-MM-DD');

    if (data.tipoPlanificador === TipoPlanificador.AUTOMATICO)
      data.fecha_fin = dayjs(data.fecha_inicio).day(7).format('YYYY-MM-DD');

    const dataSend = {
      ...data,
      flota: dataFlota?.data.codigo!,
      fecha_inicio: monday,
      tipoPlanificador: data.tipoPlanificador,
      fecha_fin: data.fecha_fin,
    }


    const res = await mutateAsync(dataSend);

    navigate(
      `${returnUrlPlanificadoresPage}/horario/${res?.data?.flota_data?.id_flota}`
    );

  };

  ///* effects -----------------------
  useEffect(() => {
    if (isLoadingPlanificador || !watchedFlota || isLoadingFlota) return;

    const fechasArrObj = planificadoresByFlotaPagingRes?.data?.data?.map(
      (item: { fecha_inicio: any; fecha_fin: any; }) => ({
        fecha_inicio: item?.fecha_inicio,
        fecha_fin: item?.fecha_fin,
      })
    ) as { fecha_inicio: string; fecha_fin: string }[];

    console.log('item: ', fechasArrObj);

    form.reset({
      ...form.getValues(),
      fechasArrObj,
    });
  }, [planificadoresByFlotaPagingRes, watchedFlota]);

  useLoaders(
    isPending ||
    isRefetchingPlanificador
  )

  if (isLoadingPlanificador || 
    isLoadingFlota) 
    return <LoaderComponent />

  return (
    <SingleFormBoxScene
      titlePage={titlePage}
      num_referencia={`FLOTA - ${dataFlota?.data.nombre}`}
      onCancel={() => {
        navigate(returnUrlPlanificadoresPage);
      }}
      onSave={handleSubmit(onSave)}
      py={0}
    >
      <SampleRadioButtonsGroup
        label="Tipo de creación"
        control={form.control}
        name="tipoPlanificador"
        defaultValue={form.getValues().tipoPlanificador || ''}
        showOptionsNode
        optionsNode={
          <RadioButtonOptions
            selectedTipoPlanificador={
              watchedTipoPlanificador as TipoPlanificador
            }
          />
        }
        onChangeValue={value => {
          // clear
          setMinDate(null);
          setMinDateManual(null);
          setInitDateManual(null);
          setEndDateManual(null);
          form.reset({
            ...form.getValues(),
            tipoPlanificador: value,
            fecha_inicio: '',
            fecha_fin: '',
          });
        }}
      />

      <>
        {!!watchedFlota ? (
          watchedTipoPlanificador === TipoPlanificador.AUTOMATICO ? (
            <>
              <SampleWeekPicker
                label="Fecha base para la semana del planificador"
                name="fecha_inicio"
                minDate={dayjs()}
                shouldDisableDate={date => {
                  const isBlockedDate = watchedFechasArrObj?.some(item =>
                    dayjs(date).isBetween(
                      dayjs(item.fecha_inicio),
                      dayjs(item.fecha_fin),
                      null,
                      '[]'
                    )
                  );

                  return !!isBlockedDate;
                }}
                control={form.control}
                defaultValue={!!minDate ? dayjs(minDate) : (null as any)}
                error={errors.fecha_inicio}
                helperText={errors.fecha_inicio?.message}
              />

              <SingleIconButton
                startIcon={<ClearIcon />}
                color="inherit"
                label="Limpiar fecha"
                onClick={() => {
                  form.reset({
                    ...form.getValues(),
                    fecha_inicio: '',
                  });
                }}
                noThemeColor
              />
            </>
          ) : watchedTipoPlanificador === TipoPlanificador.MANUAL ? (
            <Grid item container xs={12}>
              <Grid item {...gridSizeMdLg6}>
                <FormLabel component="legend">Fecha inicial</FormLabel>

                <FormControl fullWidth>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="es"
                    localeText={
                      esES.components.MuiLocalizationProvider.defaultProps
                        .localeText
                    }
                  >
                    <DateCalendar
                      views={['month', 'day']}
                      minDate={dayjs()}
                      onChange={date => {
                        setInitDateManual(date);
                        setMinDateManual(date);
                        setEndDateManual(null);
                        form.setValue(
                          'fecha_inicio',
                          date?.format('YYYY-MM-DD')
                        );
                        form.setValue('fecha_fin', '');
                      }}
                      shouldDisableDate={date => {
                        const isBlockedDate = watchedFechasArrObj?.some(item =>
                          dayjs(date).isBetween(
                            dayjs(item.fecha_inicio),
                            dayjs(item.fecha_fin),
                            null,
                            '[]'
                          )
                        );

                        return !!isBlockedDate;
                      }}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>

              <Grid item {...gridSizeMdLg6}>
                <FormLabel component="legend">Fecha final</FormLabel>

                <FormControl fullWidth>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="es"
                    localeText={
                      esES.components.MuiLocalizationProvider.defaultProps
                        .localeText
                    }
                  >
                    <DateCalendar
                      minDate={minDateManual}
                      views={['month', 'day']}
                      value={endDateManual}
                      onChange={date => {
                        setEndDateManual(date);
                        form.setValue('fecha_fin', date?.format('YYYY-MM-DD'));
                      }}
                      shouldDisableDate={date => {
                        const isBlockedDate = watchedFechasArrObj?.some(item =>
                          dayjs(date).isBetween(
                            dayjs(item.fecha_inicio),
                            dayjs(item.fecha_fin),
                            null,
                            '[]'
                          )
                        );

                        return !!isBlockedDate;
                      }}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>
            </Grid>
          ) : null
        ) : (
          <>
            <Grid item xs={12}>
              <Card
                sx={{
                  py: 2,
                  px: 3,
                  my: 1,
                }}
              >
                <Typography variant="body1">
                  Selecciona una flota para continuar.
                </Typography>
              </Card>
            </Grid>
          </>
        )}
      </>
    </SingleFormBoxScene>
  );
};

export default SavePlanificador;
